import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material';
import { MarketLink } from '@market/react';
import ProductHeading from 'components/pages/product/product_heading';
import ProductActions from 'components/pages/product/product_actions';
import ProductAlternatives from 'components/pages/product/product_alternatives';
import ProductAddOn, {
  NO_ADD_ON_VALUE,
} from 'components/pages/product/product_add_on';
import ShippingBubble from 'components/pages/product/shipping_bubble';
import SubtotalBox from 'components/pages/product/subtotal_box';
import SoftwareAddOn from 'components/pages/product/product_with_software_add_on/software_add_on';
import {
  fetchSubscriptionState,
  fetchTrialEndDate,
  SOFTWARE_SUBSCRIPTION_STATES,
  pullSoftwareFromAddOns,
} from 'utilities/software';
import SoftwareAddOnBanner from 'components/pages/product/product_with_software_add_on/software_add_on_banner';
import RichTextContent from 'components/common/contentful/rich_text_content';

import { useTranslation } from 'react-i18next';
import { localizedShopPath } from 'utilities/locales';
import { trackEvent, trackCdp } from 'utilities/tracking';
import { updateItem } from 'utilities/cart';
import {
  extractSoftwareAddOns,
  sortProductAddOnSections,
  PRE_SELECTED_ADD_ONS,
} from 'components/pages/product/product_with_software_add_on/utils';
import {
  STICKY_HEADER_OFFSET,
  TRACKING_EVENT_TYPES,
  getAddOnByVariantId,
  getAlternativeUrl,
  trackAddToCart,
  trackViewCart,
  setFooterDisclaimer,
} from 'components/pages/product/helpers';
import {
  updateCartCount,
  updateCtaSubnav,
  updateCurrentUrl,
} from 'utilities/nav';
import { usePromotionEligibility } from 'components/pages/product/hooks/use_promotion_eligibility';
import ProductPromotionBanner from 'components/common/product_promotion_banner';
import InfoBubble from 'components/common/info_bubble';
import TagIcon from '@square-icons/react/16/UI/tag';
import { useProductOptionStyles } from 'components/pages/product/product_options';
import ProductAddOnSkeleton from 'components/pages/product/product_add_on_skeleton';

const HardwareWithSoftwareAddOnOptions = ({
  cart,
  currentProduct,
  alternatives,
  currentAltMetadata,
  addOns,
  isAddOnsLoading = false,
  isAddOnsLoadingError = false,
  setCurrentProduct,
  setFinancingModalOpen,
  setToast,
}) => {
  const {
    name: productName,
    base_product_key: baseProductKey,
    slug,
    shipping: {
      out_of_stock: outOfStock,
      no_ship: noShip,
      ship_quote: shipQuote,
      expedited_available: expeditedAvailable,
    },
    tracking_data: trackingData,
    variant_token: variantToken,
    description,
    promotion_banner: promoBanner,
    add_ons_category_count: addOnsCategoryCount,
  } = currentProduct;

  const { number: orderNumber, item_count: itemCount, subtotal } = cart;

  const { t, i18n } = useTranslation();
  const classes = useProductOptionStyles();
  const theme = useTheme();
  const [requesting, setRequesting] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(itemCount);
  const [cartSubtotal, setCartSubtotal] = useState(subtotal);
  const [selection, setSelection] = useState({
    quantity: 1,
    addOns: {},
  });
  const subtotalBoxRef = useRef(null);
  const { promoEligibility } = usePromotionEligibility(cart, currentProduct);

  // this useEffect is to update selections to include the requested add-ons upon page load
  // uses a mapping of product key to pre-selected add-ons, which is defined in the utils file
  // https://www.figma.com/design/pd4ELXLMyFOnE1SUT5vAb3/Shop---Kiosk-Launch?node-id=584-36132&t=gXIJZWecEJrFDIAZ-0
  useEffect(() => {
    const updateSelectionsOnPageLoad = () => {
      const preSelectedAddOns = PRE_SELECTED_ADD_ONS[baseProductKey];

      // clear selections
      setSelection({ quantity: 1, addOns: {} });

      // add pre-selected add-ons to selections
      if (preSelectedAddOns) {
        preSelectedAddOns.forEach((category) => {
          const addOn = addOns[category];

          if (addOn) {
            setSelection((prevSelection) => ({
              ...prevSelection,
              addOns: {
                ...prevSelection.addOns,
                [category]: addOn[0].variant_id.toString(), // we will assume each addOn only has 1 option
              },
            }));
          }
        });
      }
    };

    updateSelectionsOnPageLoad();
  }, [currentProduct]);

  useEffect(() => {
    const software = pullSoftwareFromAddOns(addOns);

    software.forEach((addOn) => {
      const key = `productPage.software.disclaimer.${addOn.base_product_key}`;
      if (i18n.exists(key)) {
        setFooterDisclaimer(t(key));
      }
    });
  }, [currentProduct]);

  const onAlternativeSelected = (value) => {
    if (value === null) {
      return;
    }

    // Track alternative selected events
    const selectedAlternative = alternatives.find((alt) => alt.slug === value);
    trackEvent({
      name: 'Alternative',
      value: 'Change',
      params: {
        choice: selectedAlternative.name,
        alternativeUrl: getAlternativeUrl(selectedAlternative.slug),
        cartNumber: orderNumber,
        ...selectedAlternative.tracking_data,
      },
    });
    trackCdp({
      name: TRACKING_EVENT_TYPES.ChangeAlternative,
      params: {
        feature: 'shop',
        choice: selectedAlternative.name,
        alternativeUrl: getAlternativeUrl(selectedAlternative.slug),
        cartNumber: orderNumber,
        ...selectedAlternative.tracking_data,
      },
    });

    // Update current window path to selected alternative slug
    window.history.replaceState(
      {},
      null,
      getAlternativeUrl(selectedAlternative.slug),
    );

    // Update PublicWebNav current URL & CTA Subnav
    updateCurrentUrl(getAlternativeUrl(selectedAlternative.slug));
    updateCtaSubnav(selectedAlternative.cta_subnav);

    // Update state
    setCurrentProduct(selectedAlternative);
  };

  const onAddOnSelected = ({
    category: addOnCategory,
    newVariantId,
    choice,
  }) => {
    if (newVariantId === null) {
      return;
    }

    const newAddOns = { ...selection.addOns };
    if (newVariantId === NO_ADD_ON_VALUE) {
      delete newAddOns[addOnCategory];
      setSelection({ ...selection, addOns: newAddOns });
      return;
    } else {
      newAddOns[addOnCategory] = newVariantId;
      setSelection({ ...selection, addOns: newAddOns });
    }

    const trackingData = getAddOnByVariantId(
      addOns,
      newVariantId,
    ).tracking_data;

    trackEvent({
      name: 'Add On',
      value: 'Change',
      params: {
        choice,
        cartNumber: orderNumber,
        ...trackingData,
      },
    });

    trackCdp({
      name: TRACKING_EVENT_TYPES.ChangeAddOn,
      params: {
        feature: 'shop',
        choice,
        cartNumber: orderNumber,
        ...trackingData,
      },
    });
  };

  const onAddToCartClick = () => {
    setRequesting(true);

    const triggerSuccessToast = () => {
      const actionElement = (
        <MarketLink
          href={`${localizedShopPath()}/cart`}
          onClick={() => {
            trackViewCart({
              orderNumber,
              trackingData,
            });
          }}
        >
          {t('productPage.viewCart')}
        </MarketLink>
      );

      setToast({
        timeout: 3000,
        open: true,
        message: t('productPage.addedToCart', { name: productName }),
        variant: 'success',
        action: actionElement,
      });
    };

    const scrollToSubtotalBox = () => {
      const scrollTop =
        subtotalBoxRef.current.getBoundingClientRect().top +
        window.scrollY -
        (STICKY_HEADER_OFFSET + 25);
      window.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });
    };

    const onSuccess = (data) => {
      setRequesting(false);

      trackAddToCart({
        mainProductTrackingData: trackingData,
        addOnsTrackingData: Object.values(selection.addOns).map(
          (variantId) => getAddOnByVariantId(addOns, variantId).tracking_data,
        ),
        cartNumber: orderNumber,
        quantity: selection.quantity,
      });

      updateCartCount(data.item_count);
      setCartItemCount(data.item_count);
      setCartSubtotal(data.subtotal);
      triggerSuccessToast();
      scrollToSubtotalBox();
    };

    const onError = () => {
      setRequesting(false);
      setToast({
        timeout: 3000,
        open: true,
        message: t('productPage.addToCartError', { productName }),
        variant: 'critical',
      });
    };

    updateItem(
      variantToken,
      selection.quantity,
      Object.values(selection.addOns),
      onSuccess,
      onError,
    );
  };

  const renderAddOns = () => {
    if (
      isAddOnsLoading ||
      // The request finished loading, but the data hasn't populated yet
      (!isAddOnsLoading &&
        !isAddOnsLoadingError &&
        Object.keys(addOns).length === 0 &&
        addOnsCategoryCount > 0)
    ) {
      return (
        <div
          style={{
            marginTop: theme.spacing(4),
            marginBottom: theme.spacing(4),
          }}
        >
          {Array.from({ length: addOnsCategoryCount }, (_, index) => (
            <div key={index} style={{ marginBottom: theme.spacing(2) }}>
              <ProductAddOnSkeleton />
            </div>
          ))}
        </div>
      );
    }

    const { softwareAddOnSections, hardwareAddOnSections } =
      sortProductAddOnSections(addOns);

    const addOnComponents = [];

    if (softwareAddOnSections.length > 0) {
      softwareAddOnSections.forEach((addOnSection) => {
        addOnComponents.push(
          <SoftwareAddOn
            key={addOnSection.category}
            category={addOnSection.category}
            addOns={addOnSection.addOns}
            selected={selection.addOns[addOnSection.category]}
            onSelect={onAddOnSelected}
            yesAsFirstOption={true}
          />,
        );
      });
    }

    if (hardwareAddOnSections.length > 0) {
      hardwareAddOnSections.forEach((addOnSection) => {
        addOnComponents.push(
          <ProductAddOn
            key={addOnSection.category}
            category={addOnSection.category}
            addOns={addOnSection.addOns}
            selected={selection.addOns[addOnSection.category]}
            onSelect={onAddOnSelected}
            yesAsFirstOption={true}
            currentProduct={currentProduct}
          />,
        );
      });
    }

    return <div className={classes.fadeIn}>{addOnComponents}</div>;
  };

  const renderSoftwareAddOnBanners = () => {
    const softwareAddOns = extractSoftwareAddOns(addOns);

    if (softwareAddOns.length === 0) {
      return null;
    }

    return softwareAddOns.map((addOn) => {
      const subscriptionState = fetchSubscriptionState(addOn);
      const trialEndDate = fetchTrialEndDate(addOn);

      if (subscriptionState === SOFTWARE_SUBSCRIPTION_STATES.NOT_SUBSCRIBED) {
        return null;
      }

      return (
        <SoftwareAddOnBanner
          key={addOn.variant_id}
          softwareBaseProductKey={addOn.base_product_key}
          subscriptionState={subscriptionState}
          softwareName={addOn.name}
          trialEndDate={trialEndDate}
        />
      );
    });
  };

  const renderPromotionBanner = () => (
    <>
      <ProductPromotionBanner {...promoEligibility} slug={slug} />
      {promoBanner && (
        <InfoBubble icon={<TagIcon />}>
          <RichTextContent content={promoBanner} className="medium-20" />
        </InfoBubble>
      )}
    </>
  );

  return (
    <>
      <ProductHeading
        currentProduct={currentProduct}
        onFinancingTriggerClick={() => setFinancingModalOpen(true)}
      />
      <RichTextContent content={description} />
      {renderPromotionBanner()}
      {(Object.keys(addOns).length > 0 || alternatives.length > 0) && (
        <h2
          className={classes.fadeIn}
          style={{ marginTop: theme.spacing(3.5) }}
        >
          {t('productPage.hardwareWithSoftwareHeader')}
        </h2>
      )}
      {renderSoftwareAddOnBanners()}
      <div className={classes.fadeIn}>
        <ProductAlternatives
          currentProduct={currentProduct}
          alternatives={alternatives}
          currentAltMetadata={currentAltMetadata}
          onAlternativeSelected={onAlternativeSelected}
        />
      </div>
      {renderAddOns()}
      <ProductActions
        currentProduct={currentProduct}
        addOns={addOns}
        selection={selection}
        setSelection={setSelection}
        requesting={requesting}
        onAddToCartClick={onAddToCartClick}
      />
      <div
        style={{
          marginTop: theme.spacing(2.5),
          color: 'grey',
          fontSize: '14px',
          textAlign: 'center',
        }}
      >
        {t('productPage.software.softwareAddToCartDisclaimer')}
      </div>
      {!(outOfStock || noShip) && (
        <div
          data-testid="shipping-bubble-container"
          style={{ marginTop: theme.spacing(3.5) }}
        >
          <ShippingBubble
            quote={shipQuote}
            expeditedAvailable={expeditedAvailable}
          />
        </div>
      )}
      <div style={{ marginTop: theme.spacing(3.5) }} />
      <div ref={subtotalBoxRef}>
        <SubtotalBox
          product={currentProduct}
          cart={cart}
          subtotal={cartSubtotal}
          itemCount={cartItemCount}
        />
      </div>
    </>
  );
};

export default HardwareWithSoftwareAddOnOptions;
